import React from 'react';
import { ORDER_STATUS } from '../lib/constants';
import { cn } from '@/lib/utils';

export const StatusBadge = ({ status, label, className = '' }) => {
  const s = ORDER_STATUS[status] || { label: status, color: 'bg-gray-100 text-gray-700' };
  return (
    <span className={cn('inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold whitespace-nowrap', s.color, className)}>{label || s.label}</span>
  );
};

export const SimpleBadge = ({ children, tone = 'gray', className = '' }) => {
  const tones = {
    gray: 'bg-gray-100 text-gray-700',
    green: 'bg-emerald-100 text-emerald-700',
    red: 'bg-red-100 text-red-600',
    blue: 'bg-blue-100 text-blue-700',
    amber: 'bg-amber-100 text-amber-700',
    purple: 'bg-purple-100 text-purple-700',
  };
  return <span className={cn('inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold whitespace-nowrap', tones[tone], className)}>{children}</span>;
};

export const PageHeader = ({ title, subtitle, actions }) => (
  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
    <div>
      <h1 className="text-2xl md:text-3xl font-extrabold text-[#1E2130] tracking-tight">{title}</h1>
      {subtitle && <p className="text-gray-500 mt-1">{subtitle}</p>}
    </div>
    {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
  </div>
);

export const Card = ({ children, className = '', title, action, ...rest }) => (
  <div className={cn('bg-white rounded-2xl border border-gray-100 shadow-[0_1px_3px_rgba(16,24,40,0.06)] min-w-0', className)} {...rest}>
    {(title || action) && (
      <div className="flex items-center justify-between px-5 pt-5 pb-3">
        {title && <h3 className="font-bold text-[#1E2130] text-base">{title}</h3>}
        {action}
      </div>
    )}
    {children}
  </div>
);

export const Avatar = ({ src, name, size = 32, className = '' }) => (
  src ? (
    <img src={src} alt={name} width={size} height={size} className={cn('rounded-full object-cover shrink-0', className)} style={{ width: size, height: size }} />
  ) : (
    <div className={cn('rounded-full bg-[#FDECEC] text-[#EE3B36] font-bold flex items-center justify-center shrink-0', className)} style={{ width: size, height: size, fontSize: size / 2.6 }}>
      {(name || '?').split(' ').map((w) => w[0]).slice(0, 2).join('')}
    </div>
  )
);

export const Table = ({ columns, rows, renderRow, emptyText = 'Sin resultados' }) => (
  <div className="overflow-x-auto thin-scroll">
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-[#F7F8FA] text-gray-600 text-xs">
          {columns.map((c) => (
            <th key={c} className="text-left font-semibold px-4 py-3 first:rounded-l-lg last:rounded-r-lg whitespace-nowrap">{c}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr><td colSpan={columns.length} className="text-center text-gray-400 py-10">{emptyText}</td></tr>
        )}
        {rows.map(renderRow)}
      </tbody>
    </table>
  </div>
);

export const Tr = ({ children, className = '', ...rest }) => (
  <tr className={cn('border-b border-gray-100 last:border-0 hover:bg-[#FAFAFB] transition-colors', className)} {...rest}>{children}</tr>
);
export const Td = ({ children, className = '', ...rest }) => (
  <td className={cn('px-4 py-3 align-middle text-[#1E2130]', className)} {...rest}>{children}</td>
);

export const FilterChips = ({ options, value, onChange }) => (
  <div className="flex gap-2 flex-wrap">
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        data-testid={`filter-${o.value}`}
        onClick={() => onChange(o.value)}
        className={cn(
          'px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors',
          value === o.value ? 'bg-[#EE3B36] text-white border-[#EE3B36]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
        )}
      >
        {o.label}{typeof o.count === 'number' && <span className={cn('ml-1.5 text-xs', value === o.value ? 'text-white/80' : 'text-gray-400')}>{o.count}</span>}
      </button>
    ))}
  </div>
);

export const Stat = ({ label, value, sub, icon: Icon, bg, iconBg }) => (
  <div className={cn('rounded-2xl p-4 flex items-start gap-4', bg)}>
    <div className={cn('w-14 h-14 rounded-2xl flex items-center justify-center shrink-0', iconBg)}><Icon size={26} /></div>
    <div className="min-w-0">
      <p className="text-sm text-gray-600 font-medium truncate">{label}</p>
      <p className="text-3xl font-extrabold text-[#1E2130] leading-tight">{value}</p>
      {sub}
    </div>
  </div>
);
